import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import type { InventoryFlowResult, InventoryRefreshTargets, InventoryUserNotice } from './inventoryOperations.types';

export type InventoryRefreshHandlers = {
  loadStocks?: () => Promise<void>;
  loadPendingTransfers?: () => Promise<void>;
  loadBranchActivity?: () => Promise<void>;
  loadInventoryDashboard?: () => Promise<void>;
  refreshKpis?: () => Promise<void>;
};

export function applyInventoryUserNotice(notice: InventoryUserNotice) {
  const message = notice.message ?? '';
  if (notice.tone === 'success') showSuccess(notice.title, message);
  else if (notice.tone === 'error') showError(notice.title, message);
  else showInfo(notice.title, message);
}

export async function runInventoryRefresh(
  targets: InventoryRefreshTargets,
  handlers: InventoryRefreshHandlers
) {
  const tasks: Promise<void>[] = [];
  if (targets.stocks && handlers.loadStocks) tasks.push(handlers.loadStocks());
  if (targets.pendingTransfers && handlers.loadPendingTransfers) tasks.push(handlers.loadPendingTransfers());
  if (targets.branchActivity && handlers.loadBranchActivity) tasks.push(handlers.loadBranchActivity());
  if (targets.dashboard && (handlers.loadInventoryDashboard ?? handlers.refreshKpis)) {
    const fn = handlers.loadInventoryDashboard ?? handlers.refreshKpis;
    tasks.push(fn!());
  }
  await Promise.all(tasks);
}

export async function handleInventoryFlowResult<T>(
  result: InventoryFlowResult<T>,
  handlers: InventoryRefreshHandlers
): Promise<InventoryFlowResult<T>> {
  if (result.kind === 'blocked') {
    applyInventoryUserNotice(result.notice);
    return result;
  }
  await runInventoryRefresh(result.refresh, handlers);
  if (result.notice) applyInventoryUserNotice(result.notice);
  return result;
}

export function isInventoryFlowDone<T>(
  result: InventoryFlowResult<T>
): result is Extract<InventoryFlowResult<T>, { kind: 'done' }> {
  return result.kind === 'done';
}
